import * as React from 'react';

import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { Button } from '@rematter/ui-web';
import { TextField } from '@rematter/ui-web';
import {
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogOverlay,
} from '@rematter/ui-web';
import { useToast } from '@rematter/ui-web/hooks';
import { useAtomValue, useSetAtom } from 'jotai';

import { useCreateTransportationTypeMutation } from '~/modules/transportation/hooks/mutations/useCreateTransportationTypeMutation';
import { useUpdateTransportationTypeMutation } from '~/modules/transportation/hooks/mutations/useUpdateTransportationTypeMutation';
import {
  $resetTransportationTypeForm,
  $transportationTypeForm,
  $updateTransportationTypeForm,
} from '~/modules/transportation/state/transportationTypeForm.localState';
import { TransportationTypeFormState } from '~/modules/transportation/types';

type TransportationTypeDialogProps = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  transportationType?: TransportationTypeFormState & { id?: string };
};

export const TransportationTypeDialog = ({
  isOpen,
  onClose,
  onSuccess,
  transportationType,
}: TransportationTypeDialogProps) => {
  const toast = useToast();
  const t = useTranslations();
  const form = useAtomValue($transportationTypeForm);
  const resetForm = useSetAtom($resetTransportationTypeForm);
  const updateForm = useSetAtom($updateTransportationTypeForm);

  const { mutateAsync: createTransportationType, isPending: isCreating } =
    useCreateTransportationTypeMutation();
  const { mutateAsync: updateTransportationType, isPending: isUpdating } =
    useUpdateTransportationTypeMutation();

  React.useEffect(() => {
    if (transportationType) {
      updateForm({
        name: transportationType.name,
        description: transportationType.description,
      });
    }
  }, [transportationType, updateForm]);

  const handleClose = () => {
    onClose();
    resetForm();
  };

  const handleSave = async () => {
    try {
      if (transportationType?.id) {
        await updateTransportationType({
          id: transportationType.id,
          name: form.name,
          description: form.description,
        });
        toast.success(t.transportationTypes.messages.updateSuccess);
      } else {
        await createTransportationType({
          name: form.name,
          description: form.description,
        });
        toast.success(t.transportationTypes.messages.createSuccess);
      }
      onSuccess();
      handleClose();
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : t.transportationTypes.messages.error,
      );
    }
  };

  return (
    <DialogOverlay
      isOpen={isOpen}
      onOpenChange={handleClose}
      className="@TransportationTypeDialog"
    >
      <DialogContent slot="title" position="center">
        <DialogHeader>
          <div className="text-xl font-semibold">
            {transportationType
              ? t.transportationTypes.dialog.title.edit
              : t.transportationTypes.dialog.title.create}
          </div>
        </DialogHeader>

        <div className="py-4">
          <TextField
            label={t.transportationTypes.dialog.fields.name}
            value={form.name}
            onChange={(value: string) => updateForm({ name: value })}
            isRequired
          />
          <TextField
            label={t.transportationTypes.dialog.fields.description}
            value={form.description}
            onChange={(value: string) => updateForm({ description: value })}
            isRequired
          />
        </div>

        <DialogFooter>
          <Button
            color="failure"
            variant="outline"
            isPending={isCreating || isUpdating}
            onPress={handleClose}
          >
            {t.transportationTypes.dialog.buttons.cancel}
          </Button>
          <Button
            color="primary"
            variant="solid"
            onPress={handleSave}
            isPending={isCreating || isUpdating}
            isLoading={isCreating || isUpdating}
          >
            {transportationType
              ? t.transportationTypes.dialog.buttons.save
              : t.transportationTypes.dialog.buttons.create}
          </Button>
        </DialogFooter>
      </DialogContent>
    </DialogOverlay>
  );
};
